import React, { FC, Fragment, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, FieldArray, FieldArrayRenderProps, FormikProps } from 'formik';
import { ButtonProps } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { compose } from 'recompose';
import {
  Button,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';

import { ContentInput } from 'api';
import { PostAction, UserGroup } from 'types/app';
import { CustomSVG } from 'components';
import { IWithSocialConnections, withSocialConnections } from 'providers/queries';
import { MAX_VISIBLE_GROUPS } from 'features/posting/constants';
import { hasGroup, hasVideo } from 'features/content/helpers';

import GroupSelect from './GroupSelect';
import Connections from './Connections';

interface IProps {
  onClose: () => void;
  groups: UserGroup[];
  action: PostAction;
  onGroupToggle: (group: UserGroup, arrayHelper: FieldArrayRenderProps) => void;
}

interface IPropsEnhanced extends IProps, IWithSocialConnections, WithStyles<typeof styles> {
  formik: FormikProps<ContentInput>;
}

const styles = (theme: Theme) =>
  createStyles({
    cardContentFirst: {
      paddingBottom: 0,
    },
    cardContentSecond: {
      paddingTop: 12,
      '& > button': {
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
      },
    },
    button: {
      borderRadius: 4,
    },
    outlinedButton: {
      border: `1px solid ${theme.custom.palette.grey3}`,
      color: theme.custom.palette.grey1,
    },
    iconButton: {
      marginLeft: theme.spacing(0.5),
      padding: theme.spacing(0.5),
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
        color: theme.palette.common.white,
      },
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(
  connect,
  withSocialConnections('none'),
  withStyles(styles),
);

const Header: FC<IPropsEnhanced> = props => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();

  const { classes, formik, action, groups, onClose, onGroupToggle } = props;
  const { values } = formik;
  const { group_ids } = values;

  const isGroupSelected = hasGroup(formik.values);
  const visibleGroups = groups.slice(0, MAX_VISIBLE_GROUPS);
  const remainingGroups = groups.slice(MAX_VISIBLE_GROUPS, groups.length);
  const isCreate = action === PostAction.Create;
  const canPostShare = isCreate && !hasVideo(values);
  const headerKey = isCreate ? 'posting:createHeader' : 'posting:editHeader';

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleToggleGroup(group: UserGroup, arrayHelpers: FieldArrayRenderProps) {
    handleClose();
    onGroupToggle(group, arrayHelpers);
  }

  function renderSelectedGroup(group: UserGroup, arrayHelpers: FieldArrayRenderProps) {
    const isSelected = group_ids.some(id => id === group.id);
    const commonProps = {
      key: group.id,
      className: classes.button,
      onClick: () => handleToggleGroup(group, arrayHelpers),
    };
    const buttonProps: ButtonProps = isSelected
      ? { variant: 'contained', color: 'secondary' }
      : { variant: 'outlined', classes: { outlined: classes.outlinedButton } };

    return (
      <Button {...commonProps} {...buttonProps}>
        {group.title}
      </Button>
    );
  }

  function renderIconButton() {
    const showTooltip = !isGroupSelected && !anchorEl;

    return (
      <Tooltip open={showTooltip} title={t('posting:postToGroupError')} placement="right">
        <IconButton className={classes.iconButton} onClick={handleClick}>
          <CustomSVG name="add" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <FieldArray name="group_ids">
      {arrayHelpers => (
        <Fragment>
          <CardContent className={classes.cardContentFirst}>
            <Grid container alignItems="center" justify="space-between">
              <Grid item xs>
                <Typography variant="subtitle2">{t(headerKey)}</Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <CardContent className={classes.cardContentSecond}>
            {canPostShare && <Connections />}
            {visibleGroups.map(group => renderSelectedGroup(group, arrayHelpers))}
            {!!remainingGroups.length && renderIconButton()}
            <GroupSelect
              anchorEl={anchorEl}
              formValues={values}
              groups={remainingGroups}
              onClose={handleClose}
              onGroupToggle={group => handleToggleGroup(group, arrayHelpers)}
            />
          </CardContent>
        </Fragment>
      )}
    </FieldArray>
  );
};

export default enhancers(Header);
